import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
// import common components
import Typography, { TYPE_ELEMENTS } from "../Typography";

const TabbedCardElement = styled.div`
  display: flex;
  flex-direction: ${(props) => props.flexDirection};
  align-items: flex-start;
  justify-content: space-between;
  gap: 96px;
  width: 100%;

  @media (max-width: 1400px) {
    gap: 40px;
  }

  @media (max-width: 1280px) {
    gap: 24px;
  }

  @media (max-width: 1024px) {
    flex-direction: column;
    width: 600px;
  }

  @media (max-width: 768px) {
    width: 500px;
    gap: 20px;
  }

  @media (max-width: 640px) {
    width: 400px;
    gap: 18px;
  }

  @media (max-width: 480px) {
    width: 300px;
    gap: 16px;
  }

  //   @media (max-height: 768px) and (min-width: 480px) {
  //     flex-direction: column;
  //     width: 400px;
  //   }
`;

const CardImgDiv = styled.div`
  flex-shrink: 0;
  width: 50%;
  height: 100%;

  @media (max-width: 1024px) {
    width: 600px;
    height: 400px;
  }

  @media (max-width: 768px) {
    width: 500px;
    height: 333px;
  }

  @media (max-width: 640px) {
    width: 400px;
    height: 267px;
  }

  @media (max-width: 480px) {
    width: 300px;
    height: 200px;
  }
`;

const CardImage = styled.img`
  flex-shrink: 0;
  width: 100%;
  height: 100%;
  aspect-ratio: 3/2;
  object-fit: cover;
  border-radius: 2px;
  //   height: 488px;
`;

const SkeletonCardImage = styled(Skeleton)`
  @media (max-width: 1024px) {
    width: 600px;
    height: 400px;
  }

  @media (max-width: 768px) {
    width: 500px;
    height: 333px;
  }

  @media (max-width: 640px) {
    width: 400px;
    height: 267px;
  }

  @media (max-width: 480px) {
    width: 300px;
    height: 200px;
  }
`;

const TabbedCardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;

  @media (max-width: 1024px) {
    gap: 24px;
    width: 100%;
  }

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const TabbedBodyList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding-left: ${(props) => props.showLeftBorder === "true" && "24px"};
`;

const TabbedBodyText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  border-left: ${(props) =>
    props.showLeftBorder === "true" && "1px solid #FF4F00"};
  padding-left: ${(props) => props.showLeftBorder === "true" && "8px"};
`;

const TabbedSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;

  @media (max-width: 1024px) {
    gap: 16px;
    width: 100%;
  }

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const TabsList = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
  overflow-x: scroll;

  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */

  /* Hide scrollbar for WebKit (Chrome, Safari, Opera) */
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 1024px) {
    gap: 16px;
  }
`;

const TabItem = styled.div`
  width: fit-content;
  height: fit-content;
  color: ${(props) => (props.isActive === "true" ? "#FF4F00" : "#797979")};
  cursor: pointer;
`;

export default function TabbedCardWide({
  img = "",
  alt = "",
  title = "",
  bodyTexts = [],
  tabs = [],
  isReverse = false,
  isListed,
  id = "",
}) {
  const [currTab, setCurrTab] = useState(0);

  const srcSet = [300, 400, 500, 600, 800]
    .flatMap((w) =>
      [1, 2, 3].map(
        (dpr) =>
          `${img}?w=${w}&dpr=${dpr}&auto=format,compress ${
            w * dpr
          }w`
      )
    )
    .join(",\n");

  useEffect(() => {
    if (window) {
      if (window.innerWidth > 1024) {
        setCurrTab(0);
      }
    }
  }, [window]);

  return (
    <TabbedCardElement
      id={id}
      flexDirection={isReverse ? "row-reverse" : "row"}
    >
      <CardImgDiv>
        <CardImage 
          src={img} 
          fetchpriority="high" 
          alt={alt} 
          sizes="	
            (max-width: 480px) 300px,
            (max-width: 640px) 400px,
            (max-width: 768px) 500px,
            (max-width: 2000px) 600px,
            800px
          "
          srcSet={srcSet}
        />
      </CardImgDiv>
      <TabbedCardContent>
        <Typography typeElement={TYPE_ELEMENTS.H_5}>{title}</Typography>
        <TabbedBodyText>
          {bodyTexts.map((text, i) => (
            <Typography
              typeElement={TYPE_ELEMENTS.BODY_2}
              customWeight="300"
              key={i}
            >
              {text}
            </Typography>
          ))}
        </TabbedBodyText>
        <TabbedSection>
          <TabsList>
            {tabs.map((tab, i) => {
              const { TITLE } = tab;

              return (
                <>
                  <TabItem
                    onClick={() => setCurrTab(i)}
                    isActive={`${i === currTab}`}
                  >
                    <Typography typeElement={TYPE_ELEMENTS.H_7}>
                      {TITLE}
                    </Typography>
                  </TabItem>
                  {i !== tabs.length - 1 && (
                    <div
                      style={{
                        width: "1px",
                        height: "16px",
                        backgroundColor: "#FF4F00",
                      }}
                    />
                  )}
                </>
              );
            })}
          </TabsList>
          {tabs.map((tab, i) => {
            const { BODY, TITLE, LIST } = tab;

            if (i !== currTab) return <></>;

            return (
              <>
                <TabbedBodyText showLeftBorder={"true"}>
                  {BODY.map((text, j) => {
                    return (
                      <Typography
                        typeElement={TYPE_ELEMENTS.BODY_2}
                        customWeight="300"
                      >
                        {text}
                      </Typography>
                    );
                  })}
                  {LIST?.length > 0 && (
                    <TabbedBodyList
                      className="tabbed-body-list"
                      showLeftBorder={"true"}
                    >
                      {LIST?.map((text, j) => {
                        return (
                          <li key={j}>
                            <Typography
                              typeElement={TYPE_ELEMENTS.BODY_2}
                              customWeight="300"
                            >
                              {text}
                            </Typography>
                          </li>
                        );
                      })}
                    </TabbedBodyList>
                  )}
                </TabbedBodyText>
              </>
            );
          })}
        </TabbedSection>
      </TabbedCardContent>
    </TabbedCardElement>
  );
}
